import {
  useCallback,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { fineStep, normFromValue, quantize, valueFromNorm, type ValueScale } from "./math.js";

export type SliderProps = {
  value: number;
  min?: number;
  max?: number;
  step?: number;
  defaultValue?: number;
  label: string;
  format?: (value: number) => string;
  onChange: (value: number) => void;
  disabled?: boolean;
  className?: string;
  /** Display/drag mapping; emitted values stay in real units (e.g. Hz). */
  scale?: ValueScale;
};

export function Slider({
  value,
  min = 0,
  max = 1,
  step = 0.01,
  defaultValue,
  label,
  format = (v) => String(Math.round(v * 1000) / 1000),
  onChange,
  disabled = false,
  className,
  scale = "linear",
}: SliderProps) {
  const id = useId();
  const labelId = `${id}-label`;
  const trackRef = useRef<HTMLDivElement>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const commit = useCallback(
    (next: number) => {
      onChange(quantize(next, min, max, step));
    },
    [max, min, onChange, step],
  );

  const valueFromClientX = (clientX: number) => {
    const track = trackRef.current;
    if (!track) return value;
    const rect = track.getBoundingClientRect();
    if (rect.width <= 0) return value;
    const norm = (clientX - rect.left) / rect.width;
    return valueFromNorm(norm, min, max, step, scale);
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (disabled || editing) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    commit(valueFromClientX(event.clientX));
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (disabled || !event.currentTarget.hasPointerCapture(event.pointerId)) return;
    const next = valueFromClientX(event.clientX);
    if (event.shiftKey) {
      const unit = fineStep(step, min, max);
      const delta = next - value;
      if (Math.abs(delta) < unit) return;
      commit(value + Math.sign(delta) * unit);
      return;
    }
    commit(next);
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    const unit = event.shiftKey ? fineStep(step, min, max) : step;
    const page = unit * 10;
    switch (event.key) {
      case "ArrowRight":
      case "ArrowUp":
        event.preventDefault();
        commit(value + unit);
        break;
      case "ArrowLeft":
      case "ArrowDown":
        event.preventDefault();
        commit(value - unit);
        break;
      case "PageUp":
        event.preventDefault();
        commit(value + page);
        break;
      case "PageDown":
        event.preventDefault();
        commit(value - page);
        break;
      case "Home":
        event.preventDefault();
        commit(min);
        break;
      case "End":
        event.preventDefault();
        commit(max);
        break;
      default:
        break;
    }
  };

  const startEdit = () => {
    if (disabled) return;
    setDraft(format(value));
    setEditing(true);
  };

  const finishEdit = () => {
    const parsed = Number.parseFloat(draft);
    if (Number.isFinite(parsed)) commit(parsed);
    setEditing(false);
  };

  const norm = normFromValue(value, min, max, scale);
  const valueText = format(value);

  return (
    <div className={["flex w-full flex-col gap-1", className].filter(Boolean).join(" ")}>
      <div className="flex items-baseline justify-between gap-2">
        <div id={labelId} className="text-[11px] uppercase tracking-[0.08em] text-ink-muted">
          {label}
        </div>
        {editing ? (
          <input
            className="readout w-16 rounded-[var(--radius-control)] border border-hairline bg-panel-hi px-1 py-0.5 text-right text-xs text-ink"
            value={draft}
            autoFocus
            aria-label={`${label} value`}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={finishEdit}
            onKeyDown={(e) => {
              if (e.key === "Enter") finishEdit();
              if (e.key === "Escape") setEditing(false);
            }}
          />
        ) : (
          <button
            type="button"
            className="readout text-xs text-ink-muted hover:text-ink"
            onClick={startEdit}
            disabled={disabled}
          >
            {valueText}
          </button>
        )}
      </div>
      <div
        ref={trackRef}
        role="slider"
        tabIndex={disabled ? -1 : 0}
        aria-labelledby={labelId}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-valuetext={valueText}
        aria-orientation="horizontal"
        aria-disabled={disabled || undefined}
        className="relative h-6 touch-none select-none outline-none"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={() => defaultValue !== undefined && commit(defaultValue)}
        onKeyDown={onKeyDown}
      >
        <div className="absolute inset-x-0 top-1/2 h-[3px] -translate-y-1/2 rounded-full bg-hairline" />
        <div
          className="absolute left-0 top-1/2 h-[3px] -translate-y-1/2 rounded-full bg-signal"
          style={{ width: `${norm * 100}%` }}
        />
        <div
          className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-[var(--radius-control)] border border-hairline bg-panel-hi"
          style={{ left: `${norm * 100}%` }}
        />
      </div>
    </div>
  );
}
